import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  Divider,
  Group,
  Loader,
  NumberInput,
  Popover,
  Radio,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconCalculator,
  IconCoin,
  IconDownload,
  IconTrash,
} from '@tabler/icons-react';
import SectionLabel from '@/components/common/SectionLabel';
import FormErrorsSummary from '@/components/common/FormErrorsSummary';
import BasisPicker from '@/components/common/BasisPicker';
import UploadZone from '@/components/app/company/UploadZone';
import { orderService } from '@/api/services/OrderService';
import { userService } from '@/api/services/UserService';
import { BUY_TREE_QUERY_KEY, useBuyTree } from '@/hooks/useBuy';
import { useProfile } from '@/hooks/useProfile';
import { useIsAgent } from '@/hooks/useRoles';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { useAuth } from '@/stores/authContext';
import { formatCompanyLabel, formatMoney, formatOrgName, formatQuality } from '@/lib/format';
import { getApiMessage, getStatus, isServiceUnavailable } from '@/api/errors';
import { makeGroupUrl } from '@/lib/buyLinks';
import { flattenBuyTree, groupByBasis } from '@/lib/buyTree';
import { buildQualityAcceptance, isAcceptanceComplete } from '@/lib/buyAcceptance';
import BuyAcceptanceFields from '@/components/app/buy/BuyAcceptanceFields';
import BuyAttestationGate from '@/components/app/buy/BuyAttestationGate';
import UpdatedDateBadge from '@/components/app/buy/UpdatedDateBadge';
import { ROUTES } from '@/routes/paths';
import type { AddressOption, CompanyCard, EditPriceListRequest, OrderFile, PurchasePriceList } from '@/types/buy';
import IdText from '@/components/common/IdText';

const PRICE_MIN = 1000;
const PRICE_MAX = 999_999;

type PriceDraft = Record<string, { p0: number | string; p100: number | string }>;

const rowKey = (goodsType: string, propsSetId: number): string => `${goodsType}|${propsSetId}`;

/** Цена валидна: непустая и в платформенном диапазоне. */
const isValidPrice = (value: number | string): boolean =>
  value !== '' && Number(value) >= PRICE_MIN && Number(value) <= PRICE_MAX;

/** Поле заполнено, но вне диапазона — показываем ошибку под инпутом. */
const isOutOfRange = (value: number | string): boolean => value !== '' && !isValidPrice(value);

// Форма создания/редактирования прайс-листа покупки. Строки качества — серверные
// (шаблон onlyExisted=false), клиент только проставляет цены. Год урожая скрыт:
// при создании — текущий, при редактировании — из query.
// Секции укрупнены по смыслу (что-и-куда / цены / условия приёмки / дополнительно),
// заголовки — фирменный SectionLabel; в edit-режиме контекст одной строкой без шагов.
export default function PriceListFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const { data: profile } = useProfile();
  const isAgent = useIsAgent();
  const [params] = useSearchParams();

  // edit-режим: адрес/культура/год пришли из детали и не меняются (как на старом портале).
  const editAddress = params.get('address');
  const editAddressTitle = params.get('addressTitle');
  const editGoods = params.get('goods');
  // ВАЖНО: отсутствующий year → NaN (Number(null) даёт 0 и ложно включал edit-режим).
  const yearParam = params.get('year');
  const editYear = yearParam === null ? Number.NaN : Number(yearParam);
  const editMode = !!(editAddress && editAddressTitle && editGoods && !Number.isNaN(editYear));
  // Предзаполнение создания (вход с «+» в сводной таблице): адрес/культура из query, БЕЗ года.
  // Поля остаются редактируемыми — это подсказка, а не фиксация.
  const prefill = !editMode && !!(editAddress && editAddressTitle);

  const [selectedAddress, setSelectedAddress] = useState<AddressOption | null>(
    editMode || prefill ? { address: editAddress!, title: editAddressTitle! } : null,
  );
  const [goodsType, setGoodsType] = useState<string | null>(editMode || prefill ? editGoods : null);
  const year = editMode ? editYear : new Date().getFullYear();

  const [prices, setPrices] = useState<PriceDraft>({});
  // Дефолт — «по среднему» (самый частый вариант): минус один обязательный клик.
  const [creditType, setCreditType] = useState<string | null>('BY_AVERAGE_VALUE');
  const [byGost, setByGost] = useState(true);
  const [comment, setComment] = useState('');
  const [agree, setAgree] = useState(false);
  const [files, setFiles] = useState<OrderFile[]>([]);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Быстрый сдвиг «100% от 0%» (как старый портал): шаг N → p100 = p0 − N по всем строкам.
  const [shiftDiff, setShiftDiff] = useState<number | string>('');
  const [shiftOpened, setShiftOpened] = useState(false);

  // Кастомные надбавки/скидки приёмки (byGost=false): name → { inc, dec } в процентах.
  const [qaOverrides, setQaOverrides] = useState<Record<string, { inc: number | string; dec: number | string }>>({});

  // Грузополучатель: по умолчанию — сам покупатель; «другая организация» — поиск по названию/ИНН.
  const [consigneeType, setConsigneeType] = useState<'buyer' | 'other'>('buyer');
  const [consigneeInput, setConsigneeInput] = useState('');
  const [consigneeQuery, setConsigneeQuery] = useState('');
  const [consignee, setConsignee] = useState<CompanyCard | null>(null);

  // Несохранённые изменения: ставится только в обработчиках пользовательского ввода
  // (инициализация из шаблона — не правка). Сбрасывается перед навигацией после сабмита.
  const dirtyRef = useRef(false);

  // Дебаунс поиска грузополучателя.
  useEffect(() => {
    const handle = window.setTimeout(() => setConsigneeQuery(consigneeInput.trim()), 350);
    return () => window.clearTimeout(handle);
  }, [consigneeInput]);

  const goodsTypes = useQuery({
    queryKey: ['parentGoodsTypes'] as const,
    queryFn: ({ signal }) => orderService.getParentGoodsTypes(signal),
    enabled: isAuthenticated && !editMode,
    staleTime: 10 * 60_000,
  });

  // «Мои базисы» — все мои точки с существующими прайс-листами (из дерева закупок), без фильтра по
  // культуре: на знакомом базисе можно добавить и новую культуру. basisId берём из дерева.
  const buyTree = useBuyTree();
  const ownBases = useMemo(
    () =>
      groupByBasis(flattenBuyTree(buyTree.data)).map((base) => ({
        address: base.address,
        addressTitle: base.addressTitle,
        basisId: base.items[0]?.basisId,
      })),
    [buyTree.data],
  );

  // Шаблон прайс-листа: серверные строки качества (включая пустые) для адрес+культура+год.
  const template = useQuery({
    queryKey: ['priceListTemplate', selectedAddress?.address, selectedAddress?.title, goodsType, year] as const,
    queryFn: ({ signal }) =>
      orderService.getPurchasePriceList(
        {
          address: selectedAddress!.address,
          addressTitle: selectedAddress!.title ?? '',
          parentGoodsType: goodsType!,
          cropYear: year,
          onlyExisted: false,
        },
        signal,
      ),
    enabled: isAuthenticated && !!selectedAddress && !!goodsType,
    staleTime: 0,
    gcTime: 0,
  });

  const qaDefaults = useQuery({
    queryKey: ['qaDefaults', goodsType] as const,
    queryFn: ({ signal }) => orderService.getQaDefaults(goodsType!, signal),
    enabled: isAuthenticated && !!goodsType,
    staleTime: 5 * 60_000,
  });

  // Дефолтный грузополучатель (сам покупатель) — реквизиты для первой radio-карточки.
  const defaultConsignee = useQuery({
    queryKey: ['defaultConsignee', selectedAddress?.address, selectedAddress?.title] as const,
    queryFn: ({ signal }) =>
      orderService.getDefaultConsignee(selectedAddress!.address, selectedAddress!.title ?? '', signal),
    enabled: isAuthenticated && !!selectedAddress,
    staleTime: 5 * 60_000,
  });

  // После выбора Mantine подставляет лейбл опции в строку поиска — не ищем по нему
  // (бэкенд-поиск не понимает «Имя — ИНН …» и отвечает 500).
  const selectedConsigneeLabel = consignee?.inn
    ? formatCompanyLabel(consignee.name, consignee.inn, t('auth.fields.inn'))
    : null;
  const companySearch = useQuery({
    queryKey: ['companySearch', consigneeQuery] as const,
    queryFn: ({ signal }) => userService.searchCompanies(consigneeQuery, signal),
    enabled:
      isAuthenticated &&
      consigneeType === 'other' &&
      consigneeQuery.length >= 3 &&
      consigneeQuery !== selectedConsigneeLabel,
    staleTime: 60_000,
  });
  // «Поиск идёт» = ждём дебаунс (ввод ещё не стал запросом) ИЛИ запрос в полёте.
  const consigneeSearching =
    consigneeType === 'other' &&
    ((consigneeInput.trim().length >= 3 &&
      consigneeInput.trim() !== consigneeQuery &&
      consigneeInput.trim() !== selectedConsigneeLabel) ||
      companySearch.isFetching);

  // Выбор компании из поиска → дозапрос полной карточки по ИНН (как старый портал).
  const pickConsignee = useMutation({
    mutationFn: (inn: string) => userService.getCompanyByInn(inn),
    onSuccess: (company) => {
      dirtyRef.current = true;
      setConsignee(company);
    },
    onError: (error) =>
      notifications.show({ color: 'red', message: getApiMessage(error) ?? t('auth.errors.generic') }),
  });

  // Инициализация черновика из шаблона (цены/условия существующего прайса).
  useEffect(() => {
    const data = template.data;
    if (!data) {
      return;
    }
    const draft: PriceDraft = {};
    for (const item of data.goodsList) {
      for (const row of item.qualityRows) {
        draft[rowKey(item.goodsType, row.propsSetId)] = {
          p0: row.pricesByPrepaymentMap?.['0'] ?? '',
          p100: row.pricesByPrepaymentMap?.['100'] ?? '',
        };
      }
    }
    setPrices(draft);
    setCreditType(data.qualityCreditType ?? 'BY_AVERAGE_VALUE');
    setByGost(data.qualityAcceptanceByGost ?? true);
    setComment(data.userComment ?? '');
    // Существующие особые условия подгружаем в форму, чтобы при сохранении вернуть их в
    // specialTermsFileIds. Иначе правка цены отправила бы пустой список и стёрла уже прикреплённые
    // файлы. С org-прайсом это ещё и файлы коллег — форма показывает и возвращает весь набор заказа.
    setFiles((data.specialTermsFiles ?? []).map((file) => ({ id: file.id, name: file.name })));
  }, [template.data]);

  // Инициализация надбавок/скидок приёмки: сохранённые значения прайса (byGost=false) поверх
  // дефолтов справочника. Пересоздаётся при смене культуры/шаблона.
  useEffect(() => {
    const defaults = qaDefaults.data;
    if (!defaults) {
      return;
    }
    const saved =
      template.data && template.data.qualityAcceptanceByGost === false
        ? (template.data.qualityAcceptanceProperties ?? [])
        : [];
    const next: Record<string, { inc: number | string; dec: number | string }> = {};
    for (const property of defaults) {
      const stored = saved.find((item) => item.name === property.name);
      next[property.name] = {
        inc: stored?.increaseValue ?? property.increaseValue ?? '',
        dec: stored?.decreaseWeightValue ?? property.decreaseWeightValue ?? '',
      };
    }
    setQaOverrides(next);
  }, [qaDefaults.data, template.data]);

  // Предзаполнение грузополучателя в edit: тип НЕ приходит с бэка — определяем сравнением
  // сохранённого ИНН с ИНН дефолтного грузополучателя (как старый портал). Один раз на шаблон.
  const consigneeInitRef = useRef<PurchasePriceList | null>(null);
  useEffect(() => {
    const data = template.data;
    if (!data?.consigneeInn || !defaultConsignee.data || consigneeInitRef.current === data) {
      return;
    }
    consigneeInitRef.current = data;
    if (data.consigneeInn === defaultConsignee.data.consigneeInn) {
      setConsigneeType('buyer');
      setConsignee(null);
    } else {
      setConsigneeType('other');
      setConsignee({
        name: data.consigneeName,
        inn: data.consigneeInn,
        ogrn: data.consigneeOgrn,
        kpp: data.consigneeKpp,
        okpo: data.consigneeOkpo,
        address: data.consigneeAddress,
      });
    }
  }, [template.data, defaultConsignee.data]);

  const upload = useMutation({
    mutationFn: (selected: File[]) => orderService.uploadPurchaseSpecialTerms(selected),
    onSuccess: (uploaded) => {
      dirtyRef.current = true;
      setFiles((current) => [...current, ...uploaded]);
    },
    onError: (error) =>
      notifications.show({ color: 'red', message: getApiMessage(error) ?? t('auth.errors.generic') }),
  });

  const removeFile = useMutation({
    mutationFn: (fileId: number) => orderService.deletePurchaseFile(fileId),
    onSuccess: (_, fileId) => setFiles((current) => current.filter((file) => file.id !== fileId)),
    onError: (error) =>
      notifications.show({ color: 'red', message: getApiMessage(error) ?? t('auth.errors.generic') }),
  });

  const downloadFile = useMutation({
    mutationFn: (fileId: number) => orderService.downloadOrderFile(fileId),
    onError: (error) =>
      notifications.show({ color: 'red', message: getApiMessage(error) ?? t('auth.errors.generic') }),
  });

  // Хоть одно поле цены заполнено (включая невалидные) — для предупреждения о сбросе.
  const hasAnyInput = useMemo(
    () => Object.values(prices).some((row) => row.p0 !== '' || row.p100 !== ''),
    [prices],
  );

  // Публикуется только строка с валидной ценой; заполненные вне диапазона — блокируют сабмит.
  const publishStats = useMemo(() => {
    const rows = Object.values(prices);
    return {
      filled: rows.filter((row) => isValidPrice(row.p0) || isValidPrice(row.p100)).length,
      invalid: rows.reduce(
        (count, row) => count + (isOutOfRange(row.p0) ? 1 : 0) + (isOutOfRange(row.p100) ? 1 : 0),
        0,
      ),
      total: rows.length,
    };
  }, [prices]);

  const consigneeOk = consigneeType === 'buyer' || !!consignee?.inn;
  // «Не по ГОСТу» требует заполненных надбавки и скидки по каждому свойству (валидация бэка).
  const qaProps = qaDefaults.data ?? [];
  const qaFilled = isAcceptanceComplete(byGost, qaOverrides, qaProps);
  const canSubmit =
    publishStats.filled > 0 &&
    publishStats.invalid === 0 &&
    !!creditType &&
    qaFilled &&
    consigneeOk &&
    agree;

  // Чего не хватает для публикации — единая красная сводка ошибок формы (как на форме продажи).
  const priceErrors = [
    publishStats.filled === 0 ? t('app.buy.form.needPrice') : null,
    publishStats.invalid > 0 ? t('app.buy.form.needFixPrices') : null,
    !creditType ? t('app.buy.form.needCredit') : null,
    !qaFilled ? t('app.buy.form.needQa') : null,
    !consigneeOk ? t('app.buy.form.needConsignee') : null,
    !agree ? t('app.buy.form.needAgree') : null,
  ].filter((item): item is string => item !== null);

  // Быстрый сдвиг «100% от 0%»: по всем строкам с валидной ценой «без предоплаты».
  const shiftApplicable = Object.values(prices).some((row) => isValidPrice(row.p0));
  const applyShift = (): void => {
    const diff = Number(shiftDiff);
    if (!template.data || shiftDiff === '' || !Number.isFinite(diff) || diff <= 0) {
      return;
    }
    dirtyRef.current = true;
    setPrices((current) => {
      const next = { ...current };
      for (const item of template.data!.goodsList) {
        for (const row of item.qualityRows) {
          const key = rowKey(item.goodsType, row.propsSetId);
          const draft = next[key];
          if (draft && isValidPrice(draft.p0)) {
            // Как старый портал: цена 100% = цена 0% − шаг. Выход из диапазона
            // подсветит inline-валидация (молча не клампим).
            next[key] = { ...draft, p100: Number(draft.p0) - diff };
          }
        }
      }
      return next;
    });
    setShiftOpened(false);
  };

  // Смена культуры/адреса стирает введённые цены (черновик переинициализируется из шаблона) —
  // при заполненных полях спрашиваем подтверждение, молча данные не теряем.
  function confirmDraftReset(apply: () => void): void {
    if (!hasAnyInput) {
      apply();
      return;
    }
    modals.openConfirmModal({
      title: t('app.buy.form.resetTitle'),
      children: <Text size="sm">{t('app.buy.form.resetText')}</Text>,
      labels: { confirm: t('app.buy.form.resetConfirm'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: apply,
    });
  }

  // Защита от случайного ухода с несохранёнными изменениями (вкл. «Отмена» и «назад»).
  useUnsavedChangesGuard(() => dirtyRef.current);

  const buildPayload = (data: PurchasePriceList): EditPriceListRequest => ({
    address: data.address,
    addressTitle: data.addressTitle,
    basisId: data.basisId ?? selectedAddress?.basisId,
    agreeOffer: agree,
    qualityCreditType: creditType!,
    qualityAcceptanceByGost: byGost,
    // По ГОСТу — только gostValue; иначе — надбавка/скидка по каждому свойству (общий buildQualityAcceptance).
    qualityAcceptanceProperties: buildQualityAcceptance(byGost, qaOverrides, qaDefaults.data ?? []).qaProperties,
    specialTermsFileIds: files.map((file) => file.id),
    userComment: comment.trim() || undefined,
    // Бэкенд принимает только ИНН; для «покупателя» шлём ИНН дефолтного грузополучателя.
    consigneeInn: consigneeType === 'other' ? consignee?.inn : defaultConsignee.data?.consigneeInn,
    goodsList: data.goodsList.map((item) => ({
      goodsType: item.goodsType,
      parentGoodsType: item.parentGoodsType,
      goodsClass: item.goodsClass,
      cropYear: item.cropYear,
      qualityRows: item.qualityRows.map((row) => {
        const draft = prices[rowKey(item.goodsType, row.propsSetId)];
        const p0 = draft && draft.p0 !== '' ? Number(draft.p0) : null;
        const p100 = draft && draft.p100 !== '' ? Number(draft.p100) : null;
        return { ...row, pricesByPrepaymentMap: { '0': p0, '100': p100 } };
      }),
    })),
  });

  const submit = useMutation({
    mutationFn: () => orderService.editPriceList(buildPayload(template.data!)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUY_TREE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['purchasePriceList'] });
      queryClient.invalidateQueries({ queryKey: ['buyBestPrices'] });
      notifications.show({ color: 'lime', message: t('app.buy.form.saved') });
      dirtyRef.current = false;
      const qs = new URLSearchParams({
        address: template.data!.address,
        addressTitle: template.data!.addressTitle,
        goods: goodsType!,
        year: String(year),
      });
      navigate(`${ROUTES.APP_BUY_GROUP}?${qs.toString()}`);
    },
    onError: (error) => {
      const status = getStatus(error);
      if (status === 423) {
        notifications.show({ color: 'wheat', message: t('app.banner.certification') });
        navigate(ROUTES.APP_COMPANY_ACCREDITATION);
        return;
      }
      if (status === 406) {
        setAddressError(t('app.buy.form.errAddress'));
        return;
      }
      if (status === 412) {
        template.refetch();
        setSubmitError(t('app.buy.offer.changed'));
        return;
      }
      setSubmitError(
        isServiceUnavailable(error) ? t('auth.errors.unavailable') : (getApiMessage(error) ?? t('auth.errors.generic')),
      );
    },
  });

  function handleSubmit(): void {
    setSubmitError(null);
    if (!template.data || !canSubmit) {
      setSubmitError(t('app.buy.form.errIncomplete'));
      return;
    }
    submit.mutate();
  }

  // Агент не публикует прайс-листы (он посредник, а не сторона сделки) — на форму создания, в т.ч.
  // по прямому URL, его не пускаем: возвращаем к списку, где показано «Агент не публикует прайс-листы».
  if (isAgent) {
    return <Navigate to={ROUTES.APP_BUY} replace />;
  }

  // Ограничение по аттестации: без неё бэкенд вернёт 423 — честно говорим сразу (прямой заход по URL тоже).
  if (profile && profile.isVerified === false) {
    return (
      <Container size="md" py="xl">
        <BuyAttestationGate />
      </Container>
    );
  }

  // Аналогично для культуры: словарь грузится асинхронно, предзаполненное значение добавляем сразу.
  const goodsData = goodsType && !(goodsTypes.data ?? []).includes(goodsType)
    ? [goodsType, ...(goodsTypes.data ?? [])]
    : (goodsTypes.data ?? []);

  // Опции поиска грузополучателя; выбранная компания должна присутствовать в data.
  const consigneeData = (companySearch.data ?? [])
    .filter((item) => item.inn)
    .map((item) => ({ value: item.inn!, label: formatCompanyLabel(item.name, item.inn!, t('auth.fields.inn')) }));
  if (consignee?.inn && !consigneeData.some((option) => option.value === consignee.inn)) {
    consigneeData.unshift({
      value: consignee.inn,
      label: formatCompanyLabel(consignee.name, consignee.inn, t('auth.fields.inn')),
    });
  }

  // «Назад»/«Отмена»: из редактирования возвращаем в группу (оттуда пришли), из создания — в список.
  const backUrl = editMode
    ? makeGroupUrl({ address: editAddress!, addressTitle: editAddressTitle!, goods: editGoods!, year: editYear })
    : ROUTES.APP_BUY;
  const backLabel = editMode ? t('app.buy.offer.back') : t('app.buy.back');

  // Нумерация шагов — только в создании (в редактировании менять «куда и что» нельзя, шагов нет).
  const sectionTitle = (step: number, title: string): string => (editMode ? title : `${step} · ${title}`);

  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        <Stack gap="xs">
          <Anchor component={Link} to={backUrl} size="sm" c="brand.7">
            <Group gap={4} wrap="nowrap">
              <IconArrowLeft size={16} />
              {backLabel}
            </Group>
          </Anchor>
          <Title order={2}>{editMode ? t('app.buy.form.titleEdit') : t('app.buy.form.titleNew')}</Title>
          {editMode ? (
            // Контекст редактирования одной строкой — менять адрес/культуру нельзя.
            <Group gap={8} wrap="wrap" align="center">
              <Text fw={600}>{editGoods}</Text>
              <Text fw={500}>· {formatOrgName(editAddressTitle)}</Text>
              <Text size="sm" c="gray.7" truncate title={editAddress ?? undefined}>
                · {editAddress}
              </Text>
              <UpdatedDateBadge date={template.data?.updatedDate} />
            </Group>
          ) : null}
        </Stack>

        {/* Что и где покупаете (только создание: в edit контекст показан в шапке) */}
        {!editMode ? (
          <Card withBorder padding="lg">
            <Stack gap="md">
              <Stack gap={6}>
                <Group gap="sm">
                  <SectionLabel>{sectionTitle(1, t('app.buy.form.sectionWhere'))}</SectionLabel>
                </Group>
                <Text size="sm" c="gray.7">
                  {t('app.buy.form.addressHint')}
                </Text>
              </Stack>
              <Group grow align="flex-start" preventGrowOverflow={false} wrap="wrap">
                {/* Карточка рендерится только в create (в edit контекст — в шапке), поэтому без editMode-ветки. */}
                <BasisPicker
                  label={t('app.buy.form.address')}
                  placeholder={t('app.buy.form.addressPlaceholder')}
                  ownBases={ownBases}
                  value={
                    selectedAddress
                      ? {
                          address: selectedAddress.address,
                          addressTitle: selectedAddress.title ?? '',
                          basisId: selectedAddress.basisId,
                        }
                      : null
                  }
                  onChange={(picked) => {
                    confirmDraftReset(() => {
                      setAddressError(null);
                      setSelectedAddress(
                        picked
                          ? { address: picked.address, title: picked.addressTitle || undefined, basisId: picked.basisId }
                          : null,
                      );
                    });
                  }}
                  error={addressError}
                  miw={300}
                />
                <Select
                  label={t('app.buy.form.goods')}
                  placeholder={t('app.buy.form.goodsPlaceholder')}
                  data={goodsData}
                  value={goodsType}
                  onChange={(value) => confirmDraftReset(() => setGoodsType(value))}
                  searchable
                  miw={220}
                  maw={360}
                />
              </Group>
              {goodsType ? (
                // Ширина — по колонке полей, чтобы правый край формы не рвался.
                <Alert color="wheat" variant="light" icon={<IconAlertTriangle size={18} />} maw={560}>
                  {t('app.buy.form.immutableWarning')}
                </Alert>
              ) : null}
            </Stack>
          </Card>
        ) : null}

        {/* Цены по строкам качества (серверный шаблон) — сердце формы */}
        {selectedAddress && goodsType ? (
          template.isLoading ? (
            <Skeleton h={220} />
          ) : template.isError ? (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
              <Group justify="space-between" wrap="nowrap">
                <Text>{t('auth.errors.generic')}</Text>
                <Button variant="subtle" size="compact-sm" onClick={() => template.refetch()}>
                  {t('common.retry')}
                </Button>
              </Group>
            </Alert>
          ) : template.data ? (
            <Stack gap="xl">
              <Card withBorder padding="lg">
                <Stack gap="lg">
                  <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
                    <Stack gap={6}>
                      <Group gap="sm">
                        <SectionLabel>{sectionTitle(2, t('app.buy.form.prices'))}</SectionLabel>
                      </Group>
                      <Text size="sm" c="gray.7">
                        {t('app.buy.form.pricesHint')}
                      </Text>
                    </Stack>
                    {/* Быстрый сдвиг колонки «100%» от цен «без предоплаты» — как на старом портале. */}
                    <Popover
                      opened={shiftOpened}
                      onChange={setShiftOpened}
                      position="bottom-end"
                      withArrow
                      shadow="md"
                      width={320}
                    >
                      <Popover.Target>
                        <Button
                          variant="default"
                          size="compact-sm"
                          leftSection={<IconCalculator size={16} />}
                          onClick={() => setShiftOpened((opened) => !opened)}
                        >
                          {t('app.buy.form.shift100Button')}
                        </Button>
                      </Popover.Target>
                      <Popover.Dropdown>
                        <Stack gap="sm">
                          <Text size="sm">{t('app.buy.form.shift100Hint')}</Text>
                          <NumberInput
                            value={shiftDiff}
                            onChange={setShiftDiff}
                            min={1}
                            max={PRICE_MAX}
                            allowNegative={false}
                            allowDecimal={false}
                            thousandSeparator=" "
                            prefix="− "
                            suffix=" ₽/т"
                            placeholder={t('app.buy.form.shift100Placeholder')}
                            hideControls
                          />
                          <Button
                            size="compact-sm"
                            onClick={applyShift}
                            disabled={shiftDiff === '' || Number(shiftDiff) <= 0 || !shiftApplicable}
                          >
                            {t('app.buy.form.shift100Apply')}
                          </Button>
                        </Stack>
                      </Popover.Dropdown>
                    </Popover>
                  </Group>
                  {template.data.goodsList.map((item) => (
                    <Stack key={`${item.goodsType}-${item.cropYear}`} gap="sm">
                      {/* Подзаголовок нужен только когда класс уточняет культуру
                          («Пшеница 3 класс»); иначе он дублирует заголовок секции «Цены». */}
                      {item.goodsType !== item.parentGoodsType ? (
                        <Text fw={700} c="brand.9">
                          {item.goodsType}
                        </Text>
                      ) : null}
                      <Box visibleFrom="sm">
                        <Table withTableBorder verticalSpacing="xs">
                          <Table.Thead bg="brand.0">
                            <Table.Tr>
                              <Table.Th c="brand.9" w="40%">
                                {t('app.buy.quality')}
                              </Table.Th>
                              <Table.Th c="brand.9">{t('app.buy.prepayment0')}</Table.Th>
                              <Table.Th c="brand.9">{t('app.buy.prepayment100')}</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {item.qualityRows.map((row) => {
                              const key = rowKey(item.goodsType, row.propsSetId);
                              const draft = prices[key] ?? { p0: '', p100: '' };
                              return (
                                <Table.Tr key={row.propsSetId}>
                                  <Table.Td>{formatQuality(row.properties)}</Table.Td>
                                  {(['p0', 'p100'] as const).map((field) => (
                                    <Table.Td key={field}>
                                      <NumberInput
                                        value={draft[field]}
                                        onChange={(value) => {
                                          dirtyRef.current = true;
                                          setPrices((current) => ({
                                            ...current,
                                            [key]: { ...(current[key] ?? { p0: '', p100: '' }), [field]: value },
                                          }));
                                        }}
                                        error={isOutOfRange(draft[field]) ? t('app.buy.form.priceErr') : undefined}
                                        allowNegative={false}
                                        allowDecimal={false}
                                        thousandSeparator=" "
                                        placeholder="—"
                                        hideControls
                                        w={150}
                                      />
                                    </Table.Td>
                                  ))}
                                </Table.Tr>
                              );
                            })}
                          </Table.Tbody>
                        </Table>
                      </Box>
                      <Stack gap="sm" hiddenFrom="sm">
                        {item.qualityRows.map((row) => {
                          const key = rowKey(item.goodsType, row.propsSetId);
                          const draft = prices[key] ?? { p0: '', p100: '' };
                          return (
                            <Card key={row.propsSetId} withBorder padding="sm">
                              <Text fw={600} size="sm">
                                {formatQuality(row.properties)}
                              </Text>
                              {(['p0', 'p100'] as const).map((field) => (
                                <NumberInput
                                  key={field}
                                  label={t(field === 'p0' ? 'app.buy.prepayment0' : 'app.buy.prepayment100')}
                                  value={draft[field]}
                                  onChange={(value) => {
                                    dirtyRef.current = true;
                                    setPrices((current) => ({
                                      ...current,
                                      [key]: { ...(current[key] ?? { p0: '', p100: '' }), [field]: value },
                                    }));
                                  }}
                                  error={isOutOfRange(draft[field]) ? t('app.buy.form.priceErr') : undefined}
                                  allowNegative={false}
                                  allowDecimal={false}
                                  thousandSeparator=" "
                                  placeholder="—"
                                  hideControls
                                  mt={6}
                                />
                              ))}
                            </Card>
                          );
                        })}
                      </Stack>
                    </Stack>
                  ))}
                  <Group justify="space-between" wrap="wrap" gap="sm">
                    {/* Счётчик публикации — правило «строка без цены не публикуется» самоочевидно. */}
                    <Text fw={600} c={publishStats.filled > 0 ? 'brand.9' : 'wheat.8'}>
                      {t('app.buy.form.willPublish', {
                        count: publishStats.filled,
                        total: publishStats.total,
                      })}
                    </Text>
                    <Text size="sm" c="gray.7">
                      {t('app.buy.form.priceRange', { min: formatMoney(PRICE_MIN), max: formatMoney(PRICE_MAX) })}
                    </Text>
                  </Group>
                </Stack>
              </Card>

              {/* Условия приёмки: зачёт качества + ГОСТ — семантически одно целое */}
              <Card withBorder padding="lg">
                <Stack gap="md">
                  <SectionLabel>{sectionTitle(3, t('app.buy.form.sectionAcceptance'))}</SectionLabel>
                  <Stack gap="xs">
                    <Text fw={600}>{t('app.buy.terms.credit')}</Text>
                    <Radio.Group
                      value={creditType}
                      onChange={(value) => {
                        dirtyRef.current = true;
                        setCreditType(value);
                      }}
                      aria-label={t('app.buy.terms.credit')}
                    >
                      <Stack gap="xs">
                        <Radio value="BY_AVERAGE_VALUE" label={t('app.buy.terms.creditAverage')} />
                        <Radio value="BY_EACH_DAY" label={t('app.buy.terms.creditEachDay')} />
                      </Stack>
                    </Radio.Group>
                  </Stack>
                  <Divider />
                  <BuyAcceptanceFields
                    qaProps={qaProps}
                    byGost={byGost}
                    onByGost={(value) => {
                      dirtyRef.current = true;
                      setByGost(value);
                    }}
                    overrides={qaOverrides}
                    onOverride={(name, field, value) => {
                      dirtyRef.current = true;
                      setQaOverrides((current) => ({
                        ...current,
                        [name]: { ...(current[name] ?? { inc: '', dec: '' }), [field]: value },
                      }));
                    }}
                    loading={qaDefaults.isLoading}
                    noProps={qaDefaults.isSuccess && qaProps.length === 0}
                  />
                </Stack>
              </Card>

              {/* Грузополучатель: покупатель по умолчанию или другая организация (поиск через ДаДату) */}
              <Card withBorder padding="lg">
                <Stack gap="md">
                  <SectionLabel>{sectionTitle(4, t('app.buy.form.consignee'))}</SectionLabel>
                  <Radio.Group
                    value={consigneeType}
                    onChange={(value) => {
                      dirtyRef.current = true;
                      setConsigneeType(value === 'other' ? 'other' : 'buyer');
                    }}
                    aria-label={t('app.buy.form.consignee')}
                  >
                    <Stack gap="sm">
                      <Radio
                        value="buyer"
                        label={
                          // Дефолт определяет бэкенд по базису (терминал → его владелец, иначе покупатель).
                          <Stack gap={2}>
                            <Group gap={8} wrap="nowrap">
                              <Text fw={500}>
                                {formatOrgName(defaultConsignee.data?.consigneeName) ?? t('app.buy.form.consigneeBuyer')}
                              </Text>
                              <Badge variant="default" tt="none" fw={500} radius="sm" c="gray.7">
                                {t('app.buy.form.consigneeDefault')}
                              </Badge>
                            </Group>
                            {defaultConsignee.data?.consigneeInn ? (
                              <Text size="sm" c="gray.7">
                                {t('auth.fields.inn')} <IdText>{defaultConsignee.data.consigneeInn}</IdText>
                                {defaultConsignee.data.consigneeKpp ? (
                                  <>
                                    {` · ${t('app.company.card.kpp')} `}
                                    <IdText>{defaultConsignee.data.consigneeKpp}</IdText>
                                  </>
                                ) : null}
                              </Text>
                            ) : null}
                          </Stack>
                        }
                      />
                      <Radio value="other" label={<Text fw={500}>{t('app.buy.form.consigneeOther')}</Text>} />
                    </Stack>
                  </Radio.Group>
                  {consigneeType === 'other' ? (
                    <Stack gap="sm" pl={36}>
                      <Select
                        placeholder={t('app.buy.form.consigneeSearch')}
                        searchable
                        clearable
                        data={consigneeData}
                        filter={({ options }) => options}
                        searchValue={consigneeInput}
                        onSearchChange={setConsigneeInput}
                        value={consignee?.inn ?? null}
                        onChange={(inn) => {
                          if (inn) {
                            pickConsignee.mutate(inn);
                          } else {
                            dirtyRef.current = true;
                            setConsignee(null);
                          }
                        }}
                        // Пока дебаунс/запрос в полёте — честное «идёт поиск», а не «ничего не найдено».
                        nothingFoundMessage={
                          consigneeQuery.length < 3
                            ? t('app.buy.form.consigneeTypeMore')
                            : consigneeSearching
                              ? t('common.searching')
                              : t('app.buy.nothingFound')
                        }
                        rightSection={
                          consigneeSearching || pickConsignee.isPending ? <Loader size={16} /> : undefined
                        }
                        disabled={pickConsignee.isPending}
                        maw={560}
                      />
                      {consignee ? (
                        <Stack gap={4}>
                          <Text fw={600}>{formatOrgName(consignee.name) ?? '—'}</Text>
                          <Group gap={4} wrap="wrap">
                            {([
                              ['inn', t('auth.fields.inn')],
                              ['ogrn', 'ОГРН'],
                              ['kpp', 'КПП'],
                              ['okpo', 'ОКПО'],
                            ] as const).map(([key, label]) =>
                              consignee[key] ? (
                                <Badge key={key} variant="default" tt="none" fw={500} radius="sm" c="gray.9">
                                  {label} {consignee[key]}
                                </Badge>
                              ) : null,
                            )}
                          </Group>
                          {consignee.address ? (
                            <Text size="sm" c="gray.7">
                              {consignee.address}
                            </Text>
                          ) : null}
                        </Stack>
                      ) : null}
                      <Alert color="wheat" variant="light" icon={<IconAlertTriangle size={18} />} maw={560}>
                        {t('app.buy.form.consigneeWarning')}
                      </Alert>
                    </Stack>
                  ) : null}
                </Stack>
              </Card>

              {/* Дополнительно (необязательно): особые условия (PDF) + комментарий */}
              <Card withBorder padding="lg">
                <Stack gap="md">
                  <Group gap="sm">
                    <SectionLabel>{sectionTitle(5, t('app.buy.form.sectionExtra'))}</SectionLabel>
                    <Badge variant="default" tt="none" fw={500} radius="sm" c="gray.7">
                      {t('app.buy.form.optional')}
                    </Badge>
                  </Group>
                  <Stack gap="xs">
                    <Text fw={600}>{t('app.buy.offer.specialTerms')}</Text>
                    {files.map((file) => (
                      <Group key={file.id} justify="space-between" wrap="nowrap">
                        <Text size="sm" truncate>
                          {file.name ?? `#${file.id}`}
                        </Text>
                        {/* Пара «скачать/удалить» — как у документов в «Моя компания → Аттестация». */}
                        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={t('app.company.accreditation.download')}
                            onClick={() => downloadFile.mutate(file.id)}
                            loading={downloadFile.isPending && downloadFile.variables === file.id}
                          >
                            <IconDownload size={18} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={t('app.buy.del.button')}
                            onClick={() => removeFile.mutate(file.id)}
                            // Крутилка — только у удаляемого файла; остальные на время блокируем.
                            loading={removeFile.isPending && removeFile.variables === file.id}
                            disabled={removeFile.isPending && removeFile.variables !== file.id}
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </Group>
                      </Group>
                    ))}
                    <UploadZone
                      multiple
                      accept=".pdf"
                      hint="PDF"
                      loading={upload.isPending}
                      onFiles={(selected) => upload.mutate(selected)}
                    />
                  </Stack>
                  <Divider />
                  <Stack gap="xs">
                    <Text fw={600}>{t('app.buy.terms.comment')}</Text>
                    <Textarea
                      placeholder={t('app.buy.form.commentPlaceholder')}
                      maxLength={4096}
                      autosize
                      minRows={2}
                      value={comment}
                      onChange={(event) => {
                        dirtyRef.current = true;
                        setComment(event.currentTarget.value);
                      }}
                    />
                  </Stack>
                </Stack>
              </Card>

              {/* Комиссия (нейтральный факт, не предупреждение) + согласие */}
              <Stack gap="sm">
                {template.data.reward ? (
                  <Group gap="sm" wrap="nowrap">
                    <ThemeIcon variant="light" color="brand" size={32} radius="md" style={{ flexShrink: 0 }}>
                      <IconCoin size={18} />
                    </ThemeIcon>
                    <Text fw={500}>
                      {t('app.buy.form.commission', { value: formatMoney(template.data.reward) })}
                    </Text>
                  </Group>
                ) : null}
                <Checkbox
                  checked={agree}
                  onChange={(event) => setAgree(event.currentTarget.checked)}
                  label={<Text size="sm">{t('app.buy.offer.agreeOferta')}</Text>}
                />
              </Stack>

              {submitError ? (
                <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
                  {submitError}
                </Alert>
              ) : null}

              {/* Объяснимый сабмит: пока форму нельзя отправить — единая красная сводка, чего не хватает. */}
              <Stack gap="sm">
                <FormErrorsSummary title={t('common.fixFormErrors')} items={priceErrors} />
                <Group justify="flex-end" gap="sm">
                  <Button component={Link} to={backUrl} variant="default" disabled={submit.isPending}>
                    {t('common.cancel')}
                  </Button>
                  <Button onClick={handleSubmit} loading={submit.isPending} disabled={!canSubmit}>
                    {t('app.buy.form.save')}
                  </Button>
                </Group>
              </Stack>
            </Stack>
          ) : null
        ) : null}
      </Stack>
    </Container>
  );
}
